import React from 'react';
import { Card } from '@/components/ui/card';

interface StatsSectionProps {
  totalPlacements?: number;
  narrativeCount?: number;
  productCount?: number;
  totalProjects?: number;
  anchorCount?: number;
  supportingCount?: number;
  totalReach?: number;
  anchorReach?: number;
  supportingReach?: number;
  budget?: number;
  cpmRange?: {
    min: number;
    max: number;
    display: string;
  };
}

const StatsSection: React.FC<StatsSectionProps> = ({
  totalPlacements = 5,
  narrativeCount = 0,
  productCount = 0,
  totalProjects = 1,
  anchorCount = 1,
  supportingCount = 0,
  totalReach = 200000,
  anchorReach = 0,
  supportingReach = 200000,
  budget = 10000,
  cpmRange
}) => {
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const calculateEstimatedCPM = (budget: number, reach: number) => {
    if (reach === 0) return 0;
    return (budget / reach) * 1000;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
      {/* Placements */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">PLACEMENTS</h3>
        <div className="text-4xl font-bold text-foreground mb-2">{totalPlacements}</div>
        <div className="text-sm text-muted-foreground">
          {narrativeCount} Narrative • {productCount} Product
        </div>
      </Card>

      {/* Projects */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">PROJECTS</h3>
        <div className="text-4xl font-bold text-foreground mb-2">{totalProjects}</div>
        <div className="text-sm text-muted-foreground">
          {anchorCount} Anchor • {supportingCount} Supporting
        </div>
      </Card>

      {/* Estimated Views */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">ESTIMATED VIEWS</h3>
        
        {/* Main number and legend side by side */}
        <div className="flex items-start justify-between mb-4">
          <div className="text-4xl font-bold text-foreground">
            {formatNumber(totalReach)}
          </div>
          {totalReach > 0 && (
            <div className="flex flex-col gap-1 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                <span className="text-muted-foreground">Anchor {formatNumber(anchorReach)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-800 rounded-sm"></div>
                <span className="text-muted-foreground">Supporting {formatNumber(supportingReach)}</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Horizontal Bar Chart */}
        {totalReach > 0 && (
          <div className="w-full h-4 bg-gray-100 rounded-md overflow-hidden">
            <div className="h-full flex">
              <div
                className="bg-blue-500 h-full"
                style={{ width: `${(anchorReach / totalReach) * 100}%` }}
              ></div>
              <div
                className="bg-gray-800 h-full"
                style={{ width: `${(supportingReach / totalReach) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </Card>

      {/* Estimated CPM */}
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">ESTIMATED CPM</h3>
        <div className="text-4xl font-bold text-foreground mb-2">
          {cpmRange ? cpmRange.display : formatCurrency(calculateEstimatedCPM(budget, totalReach))}
        </div>
        <div className="text-sm text-muted-foreground">
          Based on {formatCurrency(budget)} budget
        </div>
      </Card>
    </div>
  );
};

export default StatsSection;